const DEFAULT_SCROLL_SPEED = 500

// Matched to the 0.25s page transition in mp-transitions.scss
const RESET_SCROLL_SPEED = 250

// Quintic ease-in-out formula
function quinticEaseInOut (time, start, change, duration) {
  time /= duration / 2
  if (time < 1) {
    return change / 2 * Math.pow(time, 5) + start
  }
  time -= 2
  return change / 2 * (Math.pow(time, 5) + 2) + start
}

function scrollIterate (original, target, duration = 100, start = new Date().getTime()) {
  let elapsed = new Date().getTime() - start

  // Use ease function for fanciness
  let next = quinticEaseInOut(elapsed, original, target - original, duration)
  window.scrollTo(0, next)

  // If we're within a pixel or two, snap into exact position
  if (Math.abs(next - target) < 2) {
    window.scrollTo(0, target)
    return
  }

  // If we ran out of time, stop at the right spot anyway
  if (elapsed > duration) {
    window.scrollTo(0, target)
    return
  }

  // Scroll each frame for 60fps butter
  requestAnimationFrame(() => scrollIterate(original, target, duration, start))
}

// Removes smooth scroll to top if user prefers reduced motion
function prefersReducedMotion () {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

// One tween per element. A newer tween supersedes an older one so double-taps don't fight.
const activeTweens = new WeakMap()

// Animates el.scrollTop with requestAnimationFrame instead of native `behavior: 'smooth'`.
function tweenScrollTop ({ el, target = 0, duration }) {
  const token = {}
  activeTweens.set(el, token)

  const original = el.scrollTop
  const start = new Date().getTime()

  function step () {
    if (activeTweens.get(el) !== token) {
      return
    }

    const elapsed = new Date().getTime() - start
    const next = quinticEaseInOut(elapsed, original, target - original, duration)

    // Check the clock before writing, so the ease function can't overshoot on the last frame
    if (elapsed >= duration || Math.abs(next - target) < 2) {
      el.scrollTop = target
      activeTweens.delete(el)
      return
    }

    el.scrollTop = next
    requestAnimationFrame(step)
  }

  step()
}

function safeScroll ({el, left = 0, top = 0, behavior = 'smooth'}) {
  if (typeof el === 'undefined') {
    return false
  }

  const behaviors = ['smooth', 'instant', 'auto']
  if (!behaviors.includes(behavior)) {
    console.warn('Invalid behavior for scroll. https://developer.mozilla.org/en-US/docs/Web/API/Window/scroll')
    return false
  }

  if (typeof left !== 'number' || typeof top !== 'number') {
    console.warn('Invalid top or left for scroll. https://developer.mozilla.org/en-US/docs/Web/API/Window/scroll')
    return false
  }

  el.scroll({ left: left, top: top, behavior: behavior })
  return true
}

export default {
  DEFAULT_SCROLL_SPEED,
  RESET_SCROLL_SPEED,
  scrollElementsTop: (selector, { duration = RESET_SCROLL_SPEED } = {}) => {
    const els = document.querySelectorAll(selector)
    if (els.length === 0) {
      console.warn('SmoothScroll selector returned no elements to scroll.', selector)
      return false
    }

    const animate = duration > 0 && !prefersReducedMotion()

    els.forEach(el => {
      if (animate) {
        tweenScrollTop({ el, target: 0, duration })
        return
      }
      safeScroll({ el, behavior: 'instant' })
    })

    return true
  },
  goTo: (target, duration = DEFAULT_SCROLL_SPEED) => {
    const html = document.documentElement
    const current = window.pageYOffset || html.scrollTop
    const maxScroll = html.scrollHeight - html.clientHeight
    const realTarget = Math.min(target, maxScroll)
    scrollIterate(current, realTarget, duration)
  },
  getPosition: (el) => {
    const rect = el.getBoundingClientRect()
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    return { top: rect.top + scrollTop, left: rect.left + scrollLeft }
  }
}
